import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { translateApiError } from "@/utils/apiErrors";
import { useToast } from "@/hooks/use-toast";
import { AUTH } from "@/constants/testIds";
import SEO from "@/components/seo/SEO";

export default function Register() {
  const { register } = useAuth();
  const { t, language } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password, language);
      const postAuthRedirect = localStorage.getItem("ams_post_auth_redirect");
      localStorage.removeItem("ams_post_auth_redirect");
      navigate(postAuthRedirect || "/dashboard");
    } catch (err) {
      toast({ variant: "destructive", title: t("auth.registerError"), description: translateApiError(err?.response?.data?.detail, language) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <SEO title="Créer un compte | Kalypso Music" description="Créez votre compte Kalypso Music et générez votre première chanson personnalisée par IA." path="/register" noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <img src="/logo.png" alt="Kalypso Music" className="h-10 w-10 rounded-xl object-cover" />
          <span className="font-heading text-xl font-semibold">Kalypso Music</span>
        </Link>

        <div className="rounded-2xl border border-border bg-card p-8">
          <h1 className="font-heading text-2xl font-medium">{t("auth.registerTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("auth.registerSubtitle")}</p>

          <Button variant="outline" className="mt-6 w-full rounded-full" data-testid={AUTH.googleBtn} onClick={handleGoogleLogin} type="button">
            {t("auth.googleBtn")}
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase text-muted-foreground">{t("auth.orDivider")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t("auth.nameLabel")}</Label>
              <Input id="name" required data-testid={AUTH.registerName} value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">{t("auth.emailLabel")}</Label>
              <Input id="email" type="email" autoComplete="email" required data-testid={AUTH.registerEmail} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.passwordLabel")}</Label>
              <Input id="password" type="password" required minLength={6} data-testid={AUTH.registerPassword} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" />
            </div>
            <Button type="submit" className="w-full rounded-full" data-testid={AUTH.registerSubmit} disabled={loading}>
              {loading ? t("common.loading") : t("auth.registerBtn")}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("auth.hasAccount")}{" "}
            <Link to="/login" className="font-medium text-primary hover:underline" data-testid={AUTH.switchToLogin}>
              {t("auth.signInLink")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
